import { type ComponentPropsWithoutRef, useMemo } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { SourceAttributionLabel } from "~/components/SourceAttributionLabel";
import { cn } from "~/lib/utils";
import {
  TERMS_PROSE_CLASS,
  ensureBoldHeadingSpacing,
  ensureMarkdownNewlines,
  isolateGluedTableBlocks,
  normalizeLiteralNewlines,
  normalizeTableDelimiters,
  unwrapAllCodeBlocks,
} from "~/utils/markdownTerms";
import { rehypeSourceLabels } from "~/utils/rehypeSourceLabels";

/**
 * Shared heading component overrides for consistent heading styles
 * across reports, summaries, and modals.
 */
const headingComponents: ComponentPropsWithoutRef<
  typeof Markdown
>["components"] = {
  h1: ({ node, ...props }) => (
    <h1 {...props} className="text-lg font-medium mb-2 normal-case" />
  ),
  h2: ({ node, ...props }) => (
    <h2 {...props} className="text-base font-bold mb-1" />
  ),
  h3: ({ node, ...props }) => (
    <h3 {...props} className="text-sm font-medium uppercase mb-1" />
  ),
};

type UnifiedMarkdownProps = {
  children: string;
  /** Extra className merged with TERMS_PROSE_CLASS */
  className?: string;
  /** Apply custom heading styles (h1, h2, h3). @default false */
  withHeadings?: boolean;
  /** Additional rehype plugins prepended before rehype-raw */
  extraRehypePlugins?: any[];
  /** Additional rehype plugins appended after rehype-raw (operate on parsed HTML) */
  extraRehypePluginsAfterRaw?: any[];
  /** Additional component overrides (merged after heading components if enabled) */
  components?: ComponentPropsWithoutRef<typeof Markdown>["components"];
};

export const UnifiedMarkdown = ({
  children,
  className,
  withHeadings = false,
  extraRehypePlugins = [],
  extraRehypePluginsAfterRaw = [],
  components,
}: UnifiedMarkdownProps) => {
  const rehypePlugins = [
    ...extraRehypePlugins,
    rehypeRaw,
    rehypeSourceLabels,
    ...extraRehypePluginsAfterRaw,
  ];

  const preprocessed = useMemo(
    () =>
      normalizeTableDelimiters(
        isolateGluedTableBlocks(
          ensureBoldHeadingSpacing(
            ensureMarkdownNewlines(
              unwrapAllCodeBlocks(normalizeLiteralNewlines(children))
            )
          )
        )
      ),
    [children]
  );

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={rehypePlugins}
      className={cn(TERMS_PROSE_CLASS, className)}
      components={{
        ...(withHeadings ? headingComponents : {}),
        abbr: SourceAttributionLabel,
        ...components,
      }}
    >
      {preprocessed}
    </Markdown>
  );
};
